import type { ServerApiError } from "@cede/types";

/**
 * The endpoint catalogue and the response-shape guards, on their own so a
 * consumer that only needs an endpoint name does not also carry every response
 * schema (the maps in `types.ts` reference all of them, and with them most of
 * `@cede/types` and zod).
 */

export const isServerApiError = (data: unknown): data is ServerApiError => {
  return (data as ServerApiError)?.statusCode !== undefined;
};

export type Rfc9457ProblemDetails = {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  correlationId: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
};

export const isServerApiRfcError = (data: unknown): data is Rfc9457ProblemDetails => {
  if (data === null || data === undefined || typeof data !== "object") {
    return false;
  }
  const rfcDetails = data as Rfc9457ProblemDetails;
  return (
    rfcDetails.type !== undefined &&
    rfcDetails.title !== undefined &&
    rfcDetails.status !== undefined &&
    rfcDetails.detail !== undefined &&
    rfcDetails.instance !== undefined &&
    rfcDetails.correlationId !== undefined &&
    rfcDetails.timestamp !== undefined
  );
};

export enum API_ENDPOINT {
  TON_CONNECT_VALIDATE_PROOF = "hub/auth/tonconnect/validate-proof",
  TON_CONNECT_GENERATE_PAYLOAD = "hub/auth/tonconnect/generate-payload",
  EXCHANGES_STATUS = "v1/exchange/status",
  FIAT_CURRENCIES = "v1/fiat/currencies",
  STANDARDIZED_CHAINS = "v1/exchange/map/chains",
  PRICES = "v2/price/crypto",
  SUPPORTED_TOKENS_BY_EXCHANGE = "v1/tokens/supported-tokens-by-exchange",
  SUPPORTED_NETWORKS_BY_EXCHANGE = "v1/tokens/supported-networks-by-exchange",
  TRADE_PATH = "v1/trade/path",
  ALL_FIAT_CURRENCIES = "v1/fiat/currencies/all",
  KRAKEN_WITHDRAWAL_NETWORKS_INFO = "v1/exchange/kraken/withdrawal_networks_info",
  TRADE_PATH_BY_SUPPORTED_TOKENS = "v1/trade/path-by-supported-tokens",
  LEADERBOARD = "v1/hub/leaderboard",
  TOKENS_MAPPING = "v1/tokens/mapping",
  TOKENS_METADATA = "v2/tokens/metadata",
  CAMPAIGN_OPEN_ORDERS = "v1/tradeReporting/open-orders",
  USER_STATS = "v1/user/stats",
  HUB_STATS = "v1/hub/stats",
  HUB_GLOBAL_LEADERBOARD = "v1/hub/stats/global-leaderboard",
  HUB_USER_ME = "v1/hub/users/me",
  HUB_USER_SIGNIN = "v1/hub/users/signin",
  HUB_USER_LINK_WALLET = "v1/hub/users/link-wallet",
  HUB_USER_LINK_EMAIL = "v1/hub/users/link-email",
  HUB_USER_NICKNAME = "v1/hub-user/nickname",
  HUB_USER_NICKNAME_AVAILABLE = "v1/hub-user/nickname/available",
  CEDE_ARENA_STARTER_PACK = "v1/cede-arena/starter-pack",
  HUB_USER_PREFERRED_SOCIAL = "v1/hub-user/preferred-social",
  HUB_USER_SOCIAL_VISIBILITY = "v1/hub-user/social-visibility",
  HUB_USER_PROFILE_AVATAR = "v1/hub-user/profile/avatar", // POST multipart
  HUB_USER_PROFILE_AVATAR_DELETE = "v1/hub-user/profile/avatar#delete", // DELETE; the hash picks the response schema
  HUB_USER_PROFILE_QUOTE = "v1/hub-user/profile/quote", // PUT { quote: string | null }
  WEB_PUSH_SUBSCRIPTIONS = "v1/web-push/subscriptions",
  HUB_USER_AUTOMATIC_STREAK_CGU_ACKNOWLEDGMENT = "v1/hub/users/automatic-streak-cgu-acknowledgment",
  HUB_EXCHANGE_ACCOUNT_BALANCES = "v1/hub/portfolio/balances",
  HUB_EXCHANGE_MARGIN_ASSET_MODE = "v1/hub/portfolio/margin-asset-mode",
  HUB_EXCHANGE_ACCOUNT_CHECK_CREDENTIALS = "v1/exchange-account/check-credentials",
  HUB_EXCHANGE_ACCOUNT_POSITIONS = "v1/hub/portfolio/positions",
  HUB_EXCHANGE_ACCOUNT_FUNDING_HISTORY = "v1/hub/portfolio/funding-history",
  HUB_DEPOSIT_ADDRESS = "v1/hub/portfolio/deposit-address",
  HUB_SUPPORTED_EXCHANGES = "v1/hub/exchanges/supported-exchanges",
  VALIDATE_DEFI_ADDRESS = "v1/hub/trade/validate-defi-address",
  HUB_TRADE_PERPS_PNL_OPT_IN = "v1/trade-to-earn/perps-pnl-opt-in",
  HUB_TRADE_PUBLIC_MARKET_RATE = "v1/hub/trade/public/market-rate",
  HUB_TRADE_MARKET_PAIRS = "v1/hub/trade/public/market-pairs",
  // Admin sibling of market-pairs (ApiKeyGuard, not CloudflareGuard) for server-to-server callers.
  ADMIN_HUB_TRADE_MARKET_PAIRS = "v1/admin/hub/trade/market-pairs",
  HUB_TRADE_MARKET_PAIRS_V2 = "v1/hub/trade/public/market-pairs-v2",
  HUB_TRADE_MIN_AMOUNTS = "v1/hub/trade/min-amounts",
  HUB_TRADE_PREPARE_ORDER = "v1/hub/trade/prepare-order",
  HUB_TRADE_CREATE_ORDER = "v1/hub/trade/create-order",
  HUB_TRADE_MAX_LEVERAGE = "v1/hub/trade/max-leverage",
  HUB_TRADE_VERIFY_ORDER = "v1/tradeReporting/verify-order",
  HUB_TRADE_RETRIEVE_ORDER = "v1/hub/trade/order-by-id",
  HUB_TRADE_OPEN_ORDERS = "v1/hub/trade/open-orders",
  HUB_TRADE_OHLCV = "v1/hub/trade/public/ohlcv",
  HUB_CREATE_EXCHANGE_ACCOUNT = "v1/hub/users/exchange",
  HUB_TRADE_CANCEL_ORDER = "v1/hub/trade/cancel-order",
  USER_PHASE_VOLUME = "v1/hub/leaderboard/current-user-volume-in-phase",
  HUB_OAUTH_INIT = "oauth/init",
  HUB_GET_INTERNAL_CACHE = "v1/hub/internal/cache/from-cache",
  HUB_GET_ACTIVE_CACHE_KEYS = "v1/hub/internal/cache/active-keys",
  HUB_DELETE_FROM_CACHE = "v1/hub/internal/cache/remove-cache",
  HUB_DELETE_FROM_CACHE_BULK = "v1/hub/internal/cache/remove-cache-bulk",
  HUB_REWARDS_CLAIMABLE = "v1/hub/rewards/claimable",
  HUB_REWARDS_CLAIMABLE_SOON = "v1/hub/rewards/claimable-soon",
  HUB_REWARDS_MERKL_CLAIMABLE = "v1/hub/rewards/merkl/claimable",
  HUB_SEND_EVENT = "v1/events/send",
  HUB_SEND_EVENTS_BULK = "v1/events/send-events",
  HUB_SET_PEOPLE = "v1/events/people",
  HUB_TRADE_ORDER_BOOK = "v1/hub/trade/public/order-book",
  HUB_TRADE_ORDER_BOOK_METRICS = "v1/hub/trade/public/order-book-metrics",
  HUB_TRADE_ORDER_BOOK_DEPTH = "v1/hub/trade/public/order-book-depth",
  HUB_TRADE_POPULAR_TOKENS = "v1/hub/trade/public/popular-tokens",
  HUB_TRADE_TICKER = "v1/hub/trade/public/ticker",
  HUB_TRADE_MARK_PRICE = "v1/hub/trade/public/mark-price",
  HUB_TRADE_ESTIMATE_PRICE_FROM_ORDER_BOOK = "v1/hub/trade/estimate-price-from-order-book",
  HUB_TRANSFER_ROUTES = "v1/hub/transfer/routes",
  HUB_TRANSFER_INTERNAL = "v1/hub/transfer/internal",
  HUB_BAN_RECORDS = "v1/hub/ban/records",
  HUB_BAN_RECORDS_USER_IP_HASHES = "v1/hub/ban/records/user-ip-hashes",
  HUB_BAN_RECORDS_USER_EXCHANGE_ACCOUNTS = "v1/hub/ban/records/user-exchange-accounts",
  HUB_BANNED_USERS = "v1/hub/ban/users",
  ANALYSIS_HUB_USER_SUCCESS_RATE = "v1/hub/analysis/anti-bot-success-rate-by-hub-user",
  ANALYSIS_EXCHANGE_USER_SUCCESS_RATE = "v1/hub/analysis/anti-bot-success-rate-by-exchange",
  ANALYSIS_CAMPAIGN_SUCCESS_RATE = "v1/hub/analysis/anti-bot-success-rate-by-campaign",
  ANALYSIS_IP_HASH_SUCCESS_RATE = "v1/hub/analysis/anti-bot-success-rate-by-ip-hash",
  ANALYSIS_EPOCH_SUCCESS_RATE = "v1/hub/analysis/anti-bot-success-rate-by-epoch",
  HUB_USER_AFFECTED_BY_BAN_RECORD = "v1/hub/ban/users/affected",
  EPOCHS_BY_CAMPAIGN = "v1/hub/leaderboard/epochs-by-campaign",
  HUB_USER_VOLUME_IN_WINDOW = "v1/hub/leaderboard/current-user-volume-in-window",
  HUB_REWARDS_SIMULATE = "v2/hub/rewards/simulate",
  HUB_GRAPH_OF_CONVERGING_ADDRESSES = "v1/hub/usdt-transfer-analysis/graph-of-converging-addresses",
  HUB_CONVERGING_ADDRESSES_GRAPH_IDS_AND_CAMPAIGN = "v1/hub/usdt-transfer-analysis/graph-ids-and-campaign",
  HUB_CONVERGING_ADDRESSES_REMOVE_NODE_FROM_GRAPH = "v1/hub/usdt-transfer-analysis/remove-node-from-graph",
  HUB_CONVERGING_ADDRESSES_UPDATE_GRAPHS = "v1/hub/usdt-transfer-analysis/update-converging-addresses",
  HUB_SOCIAL_OAUTH_INIT = "v1/socials/oauth/init",
  HUB_SOCIAL_OAUTH_REMOVE = "v1/socials/oauth/remove",
  HUB_SOCIAL_OAUTH_SPECIAL_TELEGRAM_CALLBACK = "v1/socials/oauth/special/telegram/callback",
  HUB_SOCIAL_OAUTH_LINK_TMA = "v1/socials/oauth/special/telegram/link-tma",
  HUB_SKYWALKER_STATS = "v1/skywalker/stats",
  HUB_SKYWALKER_EPOCH_HOURLY_VOLUME = "v1/skywalker/stats/epoch-hourly-volume",
  HUB_SKYWALKER_EPOCH_TRADERS = "v1/skywalker/stats/epoch-traders",
  HUB_SKYWALKER_CAMPAIGN_USERS = "v1/skywalker/stats/users",
  HUB_SKYWALKER_USER_VOLUME = "v1/skywalker/stats/user-volume",
  HUB_SKYWALKER_USER_GEOGRAPHY = "v1/skywalker/stats/user-geography",
  HUB_SKYWALKER_GLOBAL_USER_GEOGRAPHY = "v1/skywalker/stats/global-user-geography",
  HUB_SKYWALKER_HTE_CAMPAIGNS = "v1/skywalker/hte/campaigns",
  HUB_SKYWALKER_HTE_STATS = "v1/skywalker/hte/stats",
  HUB_SKYWALKER_HTE_INTERNAL = "v1/skywalker/hte/internal",
  HUB_REFERRAL_CODE = "v1/hub/referral/code",
  HUB_REFERRAL_ACCEPT = "v1/hub/referral/code/accept",
  HUB_REFERRAL_ELIGIBILITY = "v1/hub/referral/code/progress",
  HUB_REFERRAL_REWARDS_CLAIMABLE = "v1/hub/referral/rewards/claimable",
  HUB_REFERRALS = "v1/hub/referral/referrals",
  HUB_REFERRER_STATS = "v1/hub/referral/referrer/stats",
  HUB_REFEREE_STATS = "v1/hub/referral/referee/stats",
  HUB_REFEREE_ELIGIBILITY_PROGRESS = "v1/hub/referral/referee/progress",
  HUB_REFERER_WEEKLY_ELIGIBILITY_PROGRESS = "v1/hub/referral/referrer/weekly/progress",
  HUB_REFEREE_ELIGIBLE = "v1/hub/referral/referee/eligible",
  HUB_REFERRAL_SETTINGS = "v1/hub/referral/settings",
  HUB_REFERRAL_AMBASSADOR_ELIGIBILITY = "v1/hub/referral/ambassador-eligibility",
  HUB_REFERRAL_MONITORING_CODE_COUNT = "v1/hub/referral-monitoring/code/count",
  HUB_REFERRAL_MONITORING_REFEREES_COUNT = "v1/hub/referral-monitoring/referee/count",
  HUB_REFERRAL_MONITORING_VOLUME_BY_REFEREE = "v1/hub/referral-monitoring/referee/volume",
  HUB_REFERRAL_MONITORING_VOLUME_BY_EXCHANGE = "v1/hub/referral-monitoring/referee/volume/exchange",
  HUB_REFERRAL_MONITORING_REWARDS_STATISTICS = "v1/hub/referral-monitoring/rewards",
  HUB_REFERRAL_MONITORING_VOLUME_BY_REFERRER = "v1/hub/referral-monitoring/referrer/volume",
  HUB_REFERRAL_MONITORING_REFERRER_REFEREES = "v1/hub/referral-monitoring/referrer/referees",
  HUB_REFERRAL_MONITORING_REFERRER_DETAILS = "v1/hub/referral-monitoring/referrer/details",
  HUB_REFERRAL_MONITORING_ACTIVE_REFEREES = "v1/hub/referral-monitoring/referee/active",
  HUB_REFERRAL_MONITORING_REFERRAL_STATUS_RATE = "v1/hub/referral-monitoring/referral/status",
  HUB_REFERRAL_MONITORING_QUEST_COMPLETION_TIME = "v1/hub/referral-monitoring/referral/time",
  HUB_REFERRAL_ADMIN_GENERATE_REFERRAL_CODE = "v1/hub/referral/admin/generate-referral-code",
  QUEST_LIVE = "v1/quests/live",
  QUEST_TMA_ONBOARDING_LIVE = "v1/quests/tma-onboarding-live",
  QUEST_CLAIM_REWARDS = "v1/quests/quest-completion/claim-rewards",
  QUEST_USER_PROGRESS = "v1/quests/active/progress",
  QUEST_USER_COMPLETIONS = "v1/quests/active/completions",
  QUEST_USER_ELIGIBILITY = "v1/quests/active/eligibility",
  QUEST_X_LIKE = "v1/quests/x/like",
  QUEST_X_RETWEET = "v1/quests/x/retweet",
  QUEST_X_FOLLOW = "v1/quests/x/follow",
  QUEST_POST_DATA = "v1/quests/x/post-data",
  QUEST_COMMENT_AND_FOLLOW = "v1/quests/x/comment-and-follow",
  QUEST_POST_AND_FOLLOW = "v1/quests/x/post-and-follow",
  LOTTERY_USER_TICKETS = "v1/lottery/tickets",
  HUB_EXCHANGE_ACCOUNT_DISCONNECTION = "v1/hub/users/exchange/disconnections",
  TRADE_TO_EARN_CAMPAIGNS = "v1/trade-to-earn/campaigns",
  TRADE_TO_EARN_CAMPAIGNS_AUTHENTICATED = "v1/trade-to-earn/campaigns/authenticated",
  ADMIN_HTE_CAMPAIGNS = "v1/admin/hte/campaigns",
  ADMIN_HTE_CAMPAIGN_BY_NAME = "v1/admin/hte/campaigns/by-name",
  ADMIN_TRADE_TO_EARN_CAMPAIGNS = "v1/admin/trade-to-earn/campaigns",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_ANALYTICS_LIST = "v1/admin/trade-to-earn/campaigns/analytics-list",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_ANALYTICS_EPOCHS = "v1/admin/trade-to-earn/campaigns/analytics-epochs",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_VAULT_DETAIL = "v1/admin/trade-to-earn/campaigns/vault-detail",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_ANALYTICS_EPOCH_GROUPS = "v1/admin/trade-to-earn/campaigns/analytics-epoch-groups",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_VAULT_LIST = "v1/admin/trade-to-earn/campaigns/vault-list",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_BY_NAME = "v1/admin/trade-to-earn/campaigns/name",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_PREVIEWS = "v1/admin/trade-to-earn/campaigns/previews",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_FULL_DATA_BY_NAME = "v1/admin/trade-to-earn/campaigns/full-data/name",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_LIVE_EPOCHS = "v1/admin/trade-to-earn/campaigns/live/epochs",
  ADMIN_TRADE_TO_EARN_CAMPAIGN_VOLUME_TARGETS = "v1/admin/trade-to-earn/campaigns/volume-target-computations",
  ADMIN_REWARD_TOKEN_METADATA = "v1/admin/trade-to-earn/reward-token-metadata",
  ADMIN_REWARD_TOKEN_DEFAULT = "v1/admin/trade-to-earn/reward-token-default",
  TRADE_TO_EARN_CAMPAIGN_BY_NAME = "v1/trade-to-earn/campaigns/name",
  TRADE_TO_EARN_CAMPAIGN_BY_NAME_AUTHENTICATED = "v1/trade-to-earn/campaigns/name/authenticated",
  TRADE_TO_EARN_CAMPAIGN_BY_NAME_INTERNAL = "v1/trade-to-earn/campaigns/name/internal",
  TRADE_TO_EARN_CAMPAIGN_BY_REWARD_VAULT_ADDRESS = "v1/trade-to-earn/campaigns/reward-vault-address",
  HUB_LEADERBOARD_SIMULATE = "v1/hub/leaderboard/admin/simulate",
  HUB_LEADERBOARD_CAMPAIGNS = "v1/hub/leaderboard/admin/campaigns",
  HOLD_TO_EARN_LIVE_CAMPAIGNS = "v1/hold-to-earn/campaigns/live",
  HOLD_TO_EARN_ALL_CAMPAIGNS = "v1/hold-to-earn/campaigns",
  HOLD_TO_EARN_CAMPAIGN_BY_NAME = "v1/hold-to-earn/campaigns/by-name",
  HOLD_TO_EARN_JOIN_CAMPAIGN = "v1/hold-to-earn/campaigns/join",
  HOLD_TO_EARN_LEADERBOARD = "v1/hold-to-earn/campaigns/leaderboard",
  HOLD_TO_EARN_PARTICIPANT_STATUS = "v1/hold-to-earn/campaigns/participant-status",
  HOLD_TO_EARN_EPOCH_STATISTICS = "v1/hold-to-earn/campaigns/epoch-statistics",
  HOLD_TO_EARN_VALIDATE_DEFI_ADDRESS = "v1/hold-to-earn/validate-defi-address",
  // Main Spinning Wheel endpoints
  SPINNING_WHEEL_REWARDS = "v1/spinning-wheel/rewards",
  SPINNING_WHEEL_TICKETS = "v1/spinning-wheel/tickets",
  SPINNING_WHEEL_RESULTS = "v1/spinning-wheel/results",
  SPINNING_WHEEL_THRESHOLD_PROGRESS = "v1/spinning-wheel/threshold-progress",
  SPINNING_WHEEL_SPIN = "v1/spinning-wheel/spin",
  SPINNING_WHEEL_RECENT_WINNERS = "v1/spinning-wheel/recent-winners",
  // TMA Spinning Wheel endpoints
  SPINNING_WHEEL_TMA_ELIGIBILITY = "v1/spinning-wheel-tma/eligibility",

  PNL_SNAPSHOTS = "v1/hub/leaderboard/pnl-snapshots",
  PNL_PRICE_EVENTS = "v1/hub/leaderboard/pnl-snapshots/price-events",
  USER_PNL_STATS = "v1/hub/leaderboard/pnl-snapshots/me",
  HUB_LEADERBOARD_SHOULD_DISABLE_TRADE = "v1/hub/leaderboard/should-disable-trade",
  HUB_LEADERBOARD_MY_EPOCHS = "v1/hub/leaderboard/my-epochs",
  ARENA_OPPONENT_ACTIVITY = "v1/hub/leaderboard/opponent-activity",
  HUB_MAINTENANCE_STATUS = "v1/hub/maintenance/status",
  HUB_FEATURE_FLAGS = "v1/hub/feature-flags",
  HUB_FEATURE_FLAGS_V2 = "v2/hub/feature-flags",

  HUB_SOCIALS_ACCOUNTS = "v1/socials/accounts",
  HUB_VOUCHER_REDEEM = "v1/vouchers/redeem",

  // Exchange Account endpoints
  EXCHANGE_ACCOUNT_ADD_HYPERLIQUID = "v1/exchange-account/hyperliquid",
  EXCHANGE_ACCOUNT_APPROVE_HYPERLIQUID = "v1/exchange-account/hyperliquid/approve",
  EXCHANGE_ACCOUNT_HYPERLIQUID_STATUS = "v1/exchange-account/hyperliquid/status",
  EXCHANGE_ACCOUNT_HYPERLIQUID_ELIGIBILITY = "v1/exchange-account/hyperliquid/eligibility",
  EXCHANGE_ACCOUNT_ADD_PARADEX = "v1/exchange-account/paradex",
  EXCHANGE_ACCOUNT_GRVT_BUILDER_PREPARE = "v1/exchange-account/grvt/builder-prepare",
  EXCHANGE_ACCOUNT_GRVT_BUILDER_AUTHORIZE = "v1/exchange-account/grvt/builder-authorize",
  EXCHANGE_ACCOUNT_ZEROONE_PREPARE = "v1/exchange-account/zeroone/prepare",
  EXCHANGE_ACCOUNT_ZEROONE_AUTHORIZE = "v1/exchange-account/zeroone/authorize",
  EXCHANGE_ACCOUNT_EXTENDED_PREPARE = "v1/exchange-account/extended/prepare",
  EXCHANGE_ACCOUNT_EXTENDED_AUTHORIZE = "v1/exchange-account/extended/authorize",
  EXCHANGE_ACCOUNT_ADD_HIBACHI = "v1/exchange-account/hibachi",
  EXCHANGE_ACCOUNT_CONNECT_HIBACHI_WALLET = "v1/exchange-account/hibachi/connect-wallet",
  EXCHANGE_ACCOUNT_CONNECT_HIBACHI_WALLET_TOTP = "v1/exchange-account/hibachi/connect-wallet/totp",
  EXCHANGE_ACCOUNT_CONNECT_HIBACHI_WALLET_GRANT = "v1/exchange-account/hibachi/connect-wallet/grant",
  EXCHANGE_ACCOUNT_ADD_DECIBEL = "v1/exchange-account/decibel",
  EXCHANGE_ACCOUNT_DECIBEL_DELEGATION_PREPARE = "v1/exchange-account/decibel/delegation-prepare",
  EXCHANGE_ACCOUNT_DECIBEL_DELEGATION_AUTHORIZE = "v1/exchange-account/decibel/delegation-authorize",
  EXCHANGE_ACCOUNT_ADD_HOTSTUFF = "v1/exchange-account/hotstuff",
  EXCHANGE_ACCOUNT_APPROVE_HOTSTUFF = "v1/exchange-account/hotstuff/approve",
  EXCHANGE_ACCOUNT_KATANA_DELEGATION_PREPARE = "v1/exchange-account/katana/delegation-prepare",
  EXCHANGE_ACCOUNT_KATANA_DELEGATION_AUTHORIZE = "v1/exchange-account/katana/delegation-authorize",
  EXCHANGE_ACCOUNT_RISE_PREPARE = "v1/exchange-account/rise/prepare",
  EXCHANGE_ACCOUNT_RISE_AUTHORIZE = "v1/exchange-account/rise/authorize",

  // Admin Monitoring endpoints
  ADMIN_MONITORING_CAMPAIGNS = "v1/admin/monitoring/campaigns",
  ADMIN_MONITORING_CAMPAIGN_HEALTH = "v1/admin/monitoring/campaign-health",

  // Hall of Fame endpoints
  HALL_OF_FAME_SEASONS = "v1/hall-of-fame/seasons",
  HALL_OF_FAME_USER_RANK_DATA = "v1/hall-of-fame/user-rank-data",
  HALL_OF_FAME_TOP_50 = "v1/hall-of-fame/top-50",
  HALL_OF_FAME_COUNTDOWN_REWARDS = "v1/hall-of-fame/countdown-rewards",
  // Onchain Swap endpoints
  ONCHAIN_SWAP_QUOTE = "v1/onchain-swap/quote",
  ONCHAIN_SWAP_PORTFOLIO = "v1/onchain-swap/portfolio",
  ONCHAIN_SWAP_TOKEN_BY_ADDRESS = "v1/onchain-swap/token",
  ONCHAIN_SWAP_TOKENS_FOR_SWAP = "v1/onchain-swap/tokens-for-swap",
  ONCHAIN_SWAP_DEX_PAIRS = "v1/onchain-swap/dex-pairs",
  ONCHAIN_SWAP_SUBMIT_TRANSACTION = "v1/onchain-swap/submit-transaction",
  ONCHAIN_SWAP_WALLET_AGE_ELIGIBILITY = "v1/onchain-swap/wallet-age-eligibility",
  ONCHAIN_SWAP_SYSTEM_METADATA = "v1/onchain-swap/system-metadata",

  // Campaign Partners endpoints
  ADMIN_CAMPAIGN_PARTNERS = "v1/admin/campaign-partners",
  ADMIN_CAMPAIGN_PARTNER_BY_ID = "v1/admin/campaign-partners/by-id",

  // Solo CMS endpoints
  ADMIN_SOLO_CMS_FILES = "v1/admin/solo-cms/files",
  ADMIN_SOLO_CMS_UPLOAD = "v1/admin/solo-cms/upload",

  // Cashback endpoints
  CASHBACK_STATUS = "v1/cashback/status",
  CASHBACK_CONFIG = "v1/cashback/config",

  // Admin Cashback endpoints
  ADMIN_CASHBACK_CONFIG = "v1/admin/cashback/config",

  // Trade History endpoints
  SPOT_ORDERS = "v1/trade/history/orders",
  SPOT_TRADES = "v1/trade/history/trades",
  // Automatic Streak endpoints (V1)
  HUB_AUTOMATIC_STREAK_CREATE = "v1/hub/automatic-streak",
  HUB_AUTOMATIC_STREAK_LIST = "v1/hub/automatic-streak/list",
  HUB_AUTOMATIC_STREAK_GET = "v1/hub/automatic-streak/by-id",
  HUB_AUTOMATIC_STREAK_CANCEL = "v1/hub/automatic-streak/cancel",
  HUB_AUTOMATIC_STREAK_CONFIG = "v1/hub/automatic-streak/config",
  HUB_AUTOMATIC_STREAK_CGU_STATUS = "v1/hub/users/automatic-streak-cgu-status",
  // Automatic Streak endpoints (V2)
  HUB_AUTOMATIC_STREAK_CREATE_V2 = "v2/hub/automatic-streak",
  HUB_AUTOMATIC_STREAK_LIST_V2 = "v2/hub/automatic-streak/list", // Same endpoint as CREATE but GET method
  HUB_AUTOMATIC_STREAK_GET_V2 = "v2/hub/automatic-streak/by-id", // GET with path params /:streakId
  HUB_AUTOMATIC_STREAK_CANCEL_V2 = "v2/hub/automatic-streak/cancel",
  HUB_AUTOMATIC_STREAK_EPOCH_LIMIT = "v2/hub/automatic-streak/epoch-limit", // GET with path params /:campaignName/:epochId
  HUB_AUTOMATIC_STREAK_RECENTLY_CANCELLED = "v2/hub/automatic-streak/recently-cancelled", // GET - streaks cancelled due to config update
  HUB_AUTOMATIC_STREAK_MINIMUM_REQUIRED_BALANCE = "v2/hub/automatic-streak/minimum-required-balance", // POST - compute minimum balance
  HUB_AUTOMATIC_STREAK_JOIN_QUEUE_V2 = "v2/hub/automatic-streak/queue", // POST - opt user into the autopilot queue
  HUB_AUTOMATIC_STREAK_LEAVE_QUEUE_V2 = "v2/hub/automatic-streak/queue#leave", // DELETE with path param /:campaignName
  HUB_AUTOMATIC_STREAK_GET_QUEUE_ITEM_V2 = "v2/hub/automatic-streak/queue#item", // GET with path param /:campaignName — unified queue-row snapshot
  HUB_AUTOMATIC_STREAK_QUEUE_PRIORITY_TOPUP_V2 = "v2/hub/automatic-streak/queue#priority", // POST with path param /:campaignName/priority — spend tickets on an IN row
  HUB_AUTOMATIC_STREAK_PRIORITY_TICKETS = "v2/hub/automatic-streak/priority-tickets", // GET — unconsumed priority ticket balance
  // Hub Users admin endpoints
  ADMIN_HUB_USERS = "v1/admin/hub-users",
  ADMIN_HUB_USERS_UPDATE = "v1/admin/hub-users/update",
  ADMIN_HUB_USERS_AVATAR = "v1/admin/hub-users#avatar", // DELETE /:hubUserId/avatar
  ADMIN_HUB_USERS_QUOTE = "v1/admin/hub-users#quote", // DELETE /:hubUserId/quote
  // Blacklist endpoints
  ADMIN_BLACKLIST = "v1/admin/blacklist",
  ADMIN_BLACKLIST_BATCH_DELETE = "v1/admin/blacklist/batch-delete",
  // Whitelist endpoints
  ADMIN_WHITELIST = "v1/admin/whitelist",
  // Admin Automatic Streak endpoints
  ADMIN_AUTOMATIC_STREAK_CONFIG = "v1/admin/automatic-streak/config",
  ADMIN_AUTOMATIC_STREAK_V2_EPOCHS = "v2/admin/automatic-streak/epochs",
  ADMIN_AUTOMATIC_STREAK_V2_USERS = "v2/admin/automatic-streak/users",
  ADMIN_AUTOMATIC_STREAK_V2_TRADES = "v2/admin/automatic-streak/trades",
  ADMIN_AUTOMATIC_STREAK_V2_QUEUE_ITEMS = "v2/admin/automatic-streak/queue-items",
  ADMIN_AUTOMATIC_STREAK_V2_GRANT_PRIORITY_TICKETS = "v2/admin/automatic-streak/priority-tickets", // POST — manual ticket grant to a user

  // Quest CMS endpoints
  QUEST_CMS_SAVE = "v1/cms/quests/save",
  QUEST_CMS_DELETE = "v1/cms/quests/delete",
  QUEST_CMS_GET_BY_ID = "v1/cms/quests/get-by-id",
  QUEST_CMS_GET_BY_PAGE = "v1/cms/quests/get-by-page",
  // Admin Onchain Config endpoints
  ADMIN_ONCHAIN_CONFIG = "v1/admin/onchain-config",

  // Polymarket endpoints
  POLYMARKET_CREDENTIALS = "v1/polymarket/credentials",
  POLYMARKET_CREDENTIALS_CHECK = "v1/polymarket/credentials/check",
  POLYMARKET_ORDERS = "v1/polymarket/orders",
  POLYMARKET_EVENTS = "v1/polymarket/events",
  POLYMARKET_EVENTS_BY_IDS = "v1/polymarket/events/by-ids",

  // Cede Arena endpoints
  CEDE_ARENA_LEADERBOARD = "v1/cede-arena/leaderboard",
  CEDE_ARENA_LEADERBOARD_POSITION = "v1/cede-arena/leaderboard/position",
  CEDE_ARENA_LEADERBOARD_STATS = "v1/cede-arena/leaderboard/stats",
  CEDE_ARENA_RECENT_WINS = "v1/cede-arena/recent-wins",
  CEDE_ARENA_WEEKLY_LEADERBOARD = "v1/cede-arena/weekly-leaderboard",
  CEDE_ARENA_WEEKLY_LEADERBOARD_POSITION = "v1/cede-arena/weekly-leaderboard/position",
  CEDE_ARENA_WEEKLY_LEADERBOARD_POSITIONS = "v1/cede-arena/weekly-leaderboard/positions",
  CEDE_ARENA_WEEKLY_LEADERBOARD_CROWNS = "v1/cede-arena/weekly-leaderboard/crowns",
  CEDE_ARENA_ME_ENTRY = "v1/cede-arena/me/entry",
  CEDE_ARENA_OPT_IN = "v1/cede-arena/opt-in",
  CEDE_ARENA_TICKETS = "v1/cede-arena/tickets",
  CEDE_ARENA_REFERRALS = "v1/cede-arena/referrals",
  CEDE_ARENA_REFERRALS_APPLY = "v1/cede-arena/referrals/apply",
  CEDE_ARENA_REFERRALS_CLAIM = "v1/cede-arena/referrals/claim",
  CEDE_ARENA_KOL_MANAGER_STATUS = "v1/cede-arena/kol-manager/status",
  CEDE_ARENA_KOL_MANAGER = "v1/cede-arena/kol-manager",
  CEDE_ARENA_KOL_MANAGER_CLAIM = "v1/cede-arena/kol-manager/claim",
  CEDE_ARENA_KOL_MANAGER_VOLUME = "v1/cede-arena/kol-manager/volume",
  /** DELETE `?kolHubUserId=` unlinks a KOL. */
  CEDE_ARENA_KOL_MANAGER_KOLS = "v1/cede-arena/kol-manager/kols",
  CEDE_ARENA_KOL_MANAGER_KOL_DETAIL = "v1/cede-arena/kol-manager/kols/detail",
  CEDE_ARENA_KOL_MANAGER_KOL_SPLIT = "v1/cede-arena/kol-manager/kols/split",
  /** POST creates an invite, DELETE `?token=` revokes one. */
  CEDE_ARENA_KOL_MANAGER_INVITES = "v1/cede-arena/kol-manager/invites",
  CEDE_ARENA_KOL_INVITE = "v1/cede-arena/kol-invites",
  CEDE_ARENA_KOL_INVITE_ACCEPT = "v1/cede-arena/kol-invites/accept",
  CEDE_ARENA_KOL_MANAGER_ROLE_INVITE = "v1/cede-arena/kol-manager-role-invites",
  CEDE_ARENA_KOL_MANAGER_ROLE_INVITE_ACCEPT = "v1/cede-arena/kol-manager-role-invites/accept",
  CEDE_ARENA_CLANS = "v1/cede-arena/clans",
  CEDE_ARENA_CLAN_BADGES = "v1/cede-arena/clans/badges",
  /** Path params: `[hubUserId, "profile"]`. The anchor only disambiguates the map key. */
  CEDE_ARENA_PLAYER_PROFILE = "v1/cede-arena/players#profile",
  CEDE_ARENA_PLAYER_IDENTITIES = "v1/cede-arena/players/identities", // GET ?ids=1,2,3
  CEDE_ARENA_CLAN_ME = "v1/cede-arena/clans/me",
  CEDE_ARENA_CLAN_LEAVE = "v1/cede-arena/clans/leave",
  // GET with path param /:slug. The hash keeps the enum value unique.
  CEDE_ARENA_CLAN_BY_SLUG = "v1/cede-arena/clans#by-slug",
  // POST with path params /:slug/join.
  CEDE_ARENA_CLAN_JOIN = "v1/cede-arena/clans#join",
  // GET with path params /:slug/invite-link.
  CEDE_ARENA_CLAN_INVITE_LINK = "v1/cede-arena/clans#invite-link",
  CEDE_ARENA_QUEST_STATE = "v1/cede-arena/quest/state",
  CEDE_ARENA_QUEST_PREVIEW = "v1/cede-arena/quest/preview",
  CEDE_ARENA_TICKET_LINK = "v1/cede-arena/ticket-links",
  // POST with path params /:slug/claim — the hash keeps the enum value unique.
  CEDE_ARENA_TICKET_LINK_CLAIM = "v1/cede-arena/ticket-links#claim",
  CEDE_ARENA_CAMPAIGN_OPT_IN = "v1/cede-arena/campaigns/opt-in",
  CEDE_ARENA_CAMPAIGN_OPT_OUT = "v1/cede-arena/campaigns/opt-out",
  CEDE_ARENA_MY_CAMPAIGN_OPT_INS = "v1/cede-arena/campaigns/my-opt-ins",
  CEDE_ARENA_CAMPAIGN_OPT_IN_COUNTS = "v1/cede-arena/campaigns/opt-in-counts",
  CEDE_ARENA_CAMPAIGN_JOINERS = "v1/cede-arena/campaigns/joiners",
  CEDE_ARENA_READY = "v1/cede-arena/ready",
  CEDE_ARENA_DECLINE = "v1/cede-arena/decline",
  CEDE_ARENA_LEAVE = "v1/cede-arena/leave",
  CEDE_ARENA_ONBOARDING_COMPLETE = "v1/cede-arena/onboarding/complete",
  CEDE_ARENA_ONBOARDING_SKIP = "v1/cede-arena/onboarding/skip",
  CEDE_ARENA_ONBOARDING_STATE = "v1/cede-arena/onboarding/state",
  CEDE_ARENA_DISCORD_STATUS = "v1/cede-arena/discord-status",
  CEDE_ARENA_QUEUE_STATUS = "v1/cede-arena/queue-status",
  CEDE_ARENA_TRAFFIC_CURVE = "v1/cede-arena/traffic-curve",
  CEDE_ARENA_GEO_ELIGIBILITY = "v1/cede-arena/geo-eligibility",
  // Public and unauthenticated: both answer before a visitor has any identity.
  CONSENT_REGION = "v1/consent/region",
  CONSENT_RECORD = "v1/consent",
  // Public; fetched raw (no X-ENDPOINT) so it stays a CORS simple request.
  SERVER_TIME = "v1/time",
  // GET reads and POST submits; skip is its own route. All three return the full
  // qualification state.
  CEDE_ARENA_QUALIFICATION = "v1/cede-arena/qualification",
  CEDE_ARENA_QUALIFICATION_SKIP = "v1/cede-arena/qualification/skip",
  // GET reads and POST submits — both return the full survey state; skip returns { status }.
  CEDE_ARENA_SURVEY = "v1/cede-arena/survey",
  CEDE_ARENA_SURVEY_SKIP = "v1/cede-arena/survey/skip",
  // GET lists; PUT stars and DELETE unstars `{ pairSymbol }`. All three return the full list.
  CEDE_ARENA_MARKET_FAVORITES = "v1/cede-arena/market-favorites",
  CEDE_ARENA_REMATCH_PROPOSE = "v1/cede-arena/rematch/propose",
  // Friend graph (DEV-708). Slug rides as a pathParam on the claim; the
  // `#suffix` only disambiguates the enum (stripped from the URL).
  CEDE_ARENA_FRIENDS = "v1/cede-arena/friends",
  CEDE_ARENA_FRIENDS_LIST = "v1/cede-arena/friends/list",
  CEDE_ARENA_FRIENDS_REQUEST = "v1/cede-arena/friends/requests",
  CEDE_ARENA_FRIENDS_REQUEST_RESPOND = "v1/cede-arena/friends/requests/respond",
  CEDE_ARENA_FRIENDS_REMOVE = "v1/cede-arena/friends/remove",
  CEDE_ARENA_FRIENDS_LOOKUP = "v1/cede-arena/friends/lookup",
  CEDE_ARENA_FRIENDS_INVITE_CREATE = "v1/cede-arena/friends/invites",
  CEDE_ARENA_FRIENDS_INVITE_CLAIM = "v1/cede-arena/friends/invites#claim",
  CEDE_ARENA_REMATCH_ELIGIBILITY = "v1/cede-arena/rematch/eligibility",
  CEDE_ARENA_FORFEIT = "v1/cede-arena/forfeit",
  CEDE_ARENA_FORFEIT_ELIGIBILITY = "v1/cede-arena/forfeit/eligibility",
  // Private competitions (user-generated rooms). Slug + sub-action ride as
  // pathParams; the `#suffix` only disambiguates the enum (stripped from the URL).
  CEDE_ARENA_PRIVATE_CREATE_ROOM = "v1/cede-arena/private/rooms",
  CEDE_ARENA_PRIVATE_MY_ROOMS = "v1/cede-arena/private/rooms#mine",
  CEDE_ARENA_PRIVATE_GET_ROOM = "v1/cede-arena/private/rooms#get",
  CEDE_ARENA_PRIVATE_JOIN_ROOM = "v1/cede-arena/private/rooms#join",
  CEDE_ARENA_PRIVATE_LEAVE_ROOM = "v1/cede-arena/private/rooms#leave",
  CEDE_ARENA_PRIVATE_KICK_PARTICIPANT = "v1/cede-arena/private/rooms#kick",
  CEDE_ARENA_PRIVATE_UPDATE_CONFIG = "v1/cede-arena/private/rooms#config",
  CEDE_ARENA_PRIVATE_START = "v1/cede-arena/private/rooms#start",
  CEDE_ARENA_PRIVATE_CANCEL_ROOM = "v1/cede-arena/private/rooms#cancel",
  CEDE_ARENA_PRIVATE_RAKE_POLICY = "v1/cede-arena/private/rake-policy",
  CEDE_ARENA_FINALIZATION = "v1/cede-arena/competitions/finalization",
  CEDE_ARENA_CAMPAIGN_ACCOUNT_PNLS = "v1/cede-arena/campaign-account-pnls",
  CEDE_ARENA_BATTLE_ROYALE_STATE = "v1/cede-arena/battle-royale", // GET with path param /:campaignName
  CEDE_ARENA_CANCELLED_COMPETITION = "v1/cede-arena/cancelled-competitions", // GET with path param /:campaignName
  CEDE_ARENA_BATTLE_RECORD = "v1/cede-arena/battle-record", // GET ?campaign=&epoch= (a finished 3+ player battle)
  /** Path params: `[campaignName, "opening-move"]`. GET own, PUT arms, DELETE cancels. */
  CEDE_ARENA_OPENING_MOVE = "v1/cede-arena/campaigns#opening-move",
  CEDE_ARENA_ME_CAMPAIGN_STAKE = "v1/cede-arena/me/campaign-stake",
  CEDE_ARENA_PAYOUT_CAPS_INTERNAL = "v1/cede-arena/internal/payout-caps", // GET ?campaign= (server token only)
  ADMIN_CEDE_ARENA_ENTRIES = "v1/admin/cede-arena/entries",
  ADMIN_CEDE_ARENA_MARK_PAID = "v1/admin/cede-arena/entries/mark-paid",
  ADMIN_CEDE_ARENA_CANCEL = "v1/admin/cede-arena/entries/cancel",
  CMS_ARENA_QUEUE_DISPLAY_SETTINGS = "v1/cms/arena-queue-display-settings",
  CMS_ARENA_REENGAGEMENT_SETTINGS = "v1/cms/arena-reengagement-settings",
  CMS_ARENA_PRIVATE_COMPETITION_SETTINGS = "v1/cms/arena-private-competition-settings",
  CMS_ARENA_EVENT_SETTINGS = "v1/cms/arena-event-settings",
  CMS_ARENA_MATCHMAKING_CONFIG = "v1/cms/arena-matchmaking-config",
  CMS_ARENA_BOT_ATTRIBUTIONS = "v1/cms/arena-matchmaking-config/bot-attributions",
  CMS_ARENA_PLATFORM_SETTINGS = "v1/cms/arena-platform-settings",
  CMS_ARENA_REFERRAL_SETTINGS = "v1/cms/arena-referral-settings",
  CMS_ARENA_REFERRERS_GET_BY_PAGE = "v1/cms/arena-referrals/referrers/get-by-page",
  CMS_ARENA_REFEREES_GET_BY_PAGE = "v1/cms/arena-referrals/referees/get-by-page",
  /** Per-referrer ladder overrides. POST creates, PUT edits, DELETE ends one. */
  CMS_ARENA_REFERRAL_OVERRIDES = "v1/cms/arena-referral-settings/overrides",
  CMS_ARENA_REFERRAL_OVERRIDES_GET_BY_PAGE = "v1/cms/arena-referral-settings/overrides/get-by-page",
  /** DELETE `?hubUserId=&note=` revokes the role and unlinks every KOL. Managers are granted only through a role invite. */
  CMS_ARENA_KOL_MANAGERS = "v1/cms/arena-kol-managers",
  CMS_ARENA_KOL_MANAGERS_GET_BY_PAGE = "v1/cms/arena-kol-managers/get-by-page",
  CMS_ARENA_KOL_MANAGER_KOLS = "v1/cms/arena-kol-managers/kols",
  CMS_ARENA_KOL_MANAGER_KOLS_UNLINK = "v1/cms/arena-kol-managers/kols/unlink",
  CMS_ARENA_KOL_MANAGER_KOLS_REASSIGN = "v1/cms/arena-kol-managers/kols/reassign",
  CMS_ARENA_KOL_MANAGER_EVENTS = "v1/cms/arena-kol-managers/events",
  /** POST creates an invite to become a manager, DELETE `?token=` revokes one. */
  CMS_ARENA_KOL_MANAGER_ROLE_INVITES = "v1/cms/arena-kol-managers/role-invites",
  CMS_ARENA_KOL_MANAGER_ROLE_INVITES_GET_BY_PAGE = "v1/cms/arena-kol-managers/role-invites/get-by-page",
  CMS_ARENA_CLAN_SETTINGS = "v1/cms/arena-clan-settings",
  CMS_ARENA_CLANS_GET_BY_PAGE = "v1/cms/arena-clans/get-by-page",
  CMS_ARENA_CLAN_GET_BY_ID = "v1/cms/arena-clans/get-by-id",
  CMS_ARENA_CLAN_SAVE = "v1/cms/arena-clans/save",
  CMS_ARENA_CLAN_MEMBERS_GET_BY_PAGE = "v1/cms/arena-clans/members/get-by-page",
  CMS_ARENA_CLAN_ASSIGN_CHIEF = "v1/cms/arena-clans/assign-chief",
  CMS_ARENA_CLAN_REMOVE_MEMBER = "v1/cms/arena-clans/remove-member",
  CMS_ARENA_CLAN_TOURNAMENTS_GET_BY_PAGE = "v1/cms/arena-clan-tournaments/get-by-page",
  CMS_ARENA_CLAN_TOURNAMENT_GET_BY_ID = "v1/cms/arena-clan-tournaments/get-by-id",
  CMS_ARENA_CLAN_TOURNAMENT_SAVE = "v1/cms/arena-clan-tournaments/save",
  CMS_ARENA_SURVEY_SETTINGS = "v1/cms/arena-survey-settings",
  CMS_ARENA_SURVEY_RESPONSES = "v1/cms/arena-survey-settings/responses",
  CMS_ARENA_QUALIFICATION_SETTINGS = "v1/cms/cede-arena/qualification-settings",
  CMS_SPIN_AND_GO_CONFIG = "v1/cms/spin-and-go-config",
  CMS_SPIN_AND_GO_MULTIPLIERS = "v1/cms/spin-and-go-multipliers",
  CMS_SPIN_AND_GO_MULTIPLIERS_VOID = "v1/cms/spin-and-go-multipliers/void",
  SPIN_AND_GO_PUBLIC_CONFIG = "v1/spin-and-go/public-config",
  CEDE_ARENA_MATCHMAKING_TIERS = "v1/cede-arena/matchmaking-tiers",
  ADMIN_CEDE_ARENA_BLUEPRINTS = "v1/admin/cede-arena/blueprints",
  ADMIN_CEDE_ARENA_BLUEPRINT_PREVIEWS = "v1/admin/cede-arena/blueprints/previews",
  ADMIN_CEDE_ARENA_BLUEPRINT_BY_ID = "v1/admin/cede-arena/blueprints/by-id",
  ADMIN_CEDE_ARENA_UNLINKED_COMPETITIONS = "v1/admin/cede-arena/competitions/unlinked",
  ADMIN_CEDE_ARENA_COMPETITIONS = "v1/admin/cede-arena/competitions",
  ADMIN_CEDE_ARENA_WHITELIST = "v1/admin/cede-arena/whitelist",

  // Cede Arena ops dashboard (DEV-691) — read-only admin panels, one endpoint per panel.
  ADMIN_ARENA_OPS_OVERVIEW_KPIS = "v1/admin/arena-ops/overview/kpis",
  ADMIN_ARENA_OPS_OVERVIEW_COVERAGE_RINGS = "v1/admin/arena-ops/overview/coverage-rings",
  ADMIN_ARENA_OPS_OVERVIEW_RECONCILIATION = "v1/admin/arena-ops/overview/reconciliation",
  ADMIN_ARENA_OPS_USERS = "v1/admin/arena-ops/users",
  ADMIN_ARENA_OPS_USERS_KPIS = "v1/admin/arena-ops/users/kpis",
  ADMIN_ARENA_OPS_USERS_LEDGER_IDENTITY = "v1/admin/arena-ops/users/ledger-identity",
  ADMIN_ARENA_OPS_USERS_BALANCE_DISTRIBUTION = "v1/admin/arena-ops/users/balance-distribution",
  ADMIN_ARENA_OPS_USERS_MOVEMENTS = "v1/admin/arena-ops/users/movements",
  ADMIN_ARENA_OPS_MONEY_PERIOD_FLOWS = "v1/admin/arena-ops/money/period-flows",
  ADMIN_ARENA_OPS_MONEY_HOUSE_INJECTION = "v1/admin/arena-ops/money/house-injection",
  ADMIN_ARENA_OPS_MONEY_RAKE_PROVENANCE = "v1/admin/arena-ops/money/rake-provenance",
  ADMIN_ARENA_OPS_MONEY_REVENUE_PER_DAY = "v1/admin/arena-ops/money/revenue-per-day",
  ADMIN_ARENA_OPS_MONEY_DEPOSITS = "v1/admin/arena-ops/money/deposits",
  ADMIN_ARENA_OPS_MONEY_WITHDRAWALS = "v1/admin/arena-ops/money/withdrawals",
  ADMIN_ARENA_OPS_MONEY_FREE_TICKET_VALUE = "v1/admin/arena-ops/money/free-ticket-value",
  ADMIN_ARENA_OPS_MONEY_SANKEY = "v1/admin/arena-ops/money/sankey",
  ADMIN_ARENA_OPS_MONEY_MOVEMENTS = "v1/admin/arena-ops/money/movements",
  ADMIN_ARENA_OPS_MONEY_WITHDRAWAL_REQUESTS = "v1/admin/arena-ops/money/withdrawal-requests",
  ADMIN_ARENA_OPS_TICKETS_LIFECYCLE = "v1/admin/arena-ops/tickets/lifecycle",
  ADMIN_ARENA_OPS_TICKETS_BY_SOURCE = "v1/admin/arena-ops/tickets/by-source",
  ADMIN_ARENA_OPS_TICKET_LINKS = "v1/admin/arena-ops/tickets/links",
  ADMIN_ARENA_OPS_TICKETS_OUTCOMES = "v1/admin/arena-ops/tickets/outcomes",
  ADMIN_ARENA_OPS_MATCHMAKING_UNIVERSE = "v1/admin/arena-ops/matchmaking/universe",
  ADMIN_ARENA_OPS_MATCHMAKING_PAIRING_RATE_SERIES = "v1/admin/arena-ops/matchmaking/pairing-rate-series",
  ADMIN_ARENA_OPS_MATCHMAKING_WAIT_SERIES = "v1/admin/arena-ops/matchmaking/wait-series",
  ADMIN_ARENA_OPS_MATCHMAKING_WAIT_CDF = "v1/admin/arena-ops/matchmaking/wait-cdf",
  ADMIN_ARENA_OPS_MATCHMAKING_ENTRIES_BY_BUY_IN = "v1/admin/arena-ops/matchmaking/entries-by-buy-in",
  ADMIN_ARENA_OPS_MATCHMAKING_QUEUE_HEALTH = "v1/admin/arena-ops/matchmaking/queue-health",
  ADMIN_ARENA_OPS_MATCHMAKING_MATCHES_BY_HOUR = "v1/admin/arena-ops/matchmaking/matches-by-hour",
  ADMIN_ARENA_OPS_MATCHMAKING_QUEUE_DEPTH = "v1/admin/arena-ops/matchmaking/queue-depth",
  CMS_CANBERRAI_CONFIG = "v1/cms/canberrai-config",
  CMS_CANBERRAI_PLAYER_DIFFICULTY = "v1/cms/canberrai-config/player-difficulty",
  CMS_EXPERIMENTS = "v1/cms/experiments",
  CMS_ARENA_STARTER_PACK = "v1/cms/arena-starter-pack",
  CMS_ARENA_QUEST_REWARD = "v1/cms/cede-arena/quest-reward",
  CMS_ARENA_TICKET_CONFIGURATION = "v1/cms/arena-ticket-configuration",
  CMS_ARENA_TICKETS_GRANT = "v1/cms/arena-tickets/grant",
  CMS_ARENA_TICKETS_FOR_USER = "v1/cms/arena-tickets/user",
  ARENA_AFFILIATE_APPLICATION_SUBMIT = "v1/arena-affiliate-applications/submit",
  CMS_ARENA_AFFILIATE_APPLICATIONS_BY_PAGE = "v1/cms/arena-affiliate-applications/get-by-page",
  CMS_ARENA_AFFILIATE_APPLICATION_BY_ID = "v1/cms/arena-affiliate-applications/get-by-id",
  CMS_ARENA_AFFILIATE_APPLICATION_SAVE_REVIEW = "v1/cms/arena-affiliate-applications/save-review",
  BIG_ARENA_COMPETITION_MY_REGISTRATION = "v1/big-arena-competition/registration",
  BIG_ARENA_COMPETITION_MY_REGISTRATION_SUBMIT = "v1/big-arena-competition/registration#submit", // POST on the GET path; the hash picks the response schema
  CMS_BIG_ARENA_COMPETITION_REGISTRATIONS_BY_PAGE = "v1/cms/big-arena-competition/registrations/get-by-page",
  CMS_BIG_ARENA_COMPETITION_REGISTRATION_REVIEW = "v1/cms/big-arena-competition/registrations/review",
  CMS_BIG_ARENA_COMPETITION_PRIORITY_CODES_BY_PAGE = "v1/cms/big-arena-competition/priority-codes/get-by-page",
  CMS_BIG_ARENA_COMPETITION_PRIORITY_CODES_GENERATE = "v1/cms/big-arena-competition/priority-codes/generate",
  CMS_BIG_ARENA_COMPETITION_PRIORITY_CODE_UPDATE_LABEL = "v1/cms/big-arena-competition/priority-codes/update-label",
  CMS_BIG_ARENA_COMPETITION_PRIORITY_CODE_DISABLE = "v1/cms/big-arena-competition/priority-codes/disable",
  // Arena tournaments. Nested routes share a base path; the hash picks the schema and `pathParams` carries the rest.
  ARENA_TOURNAMENT_ACTIVE = "v1/cede-arena/tournaments/active",
  ARENA_TOURNAMENT_CURRENT = "v1/cede-arena/tournaments/current",
  ARENA_TOURNAMENT_GET = "v1/cede-arena/tournaments",
  ARENA_TOURNAMENT_ROUND_LEADERBOARD = "v1/cede-arena/tournaments#round-leaderboard",
  ARENA_TOURNAMENT_SNIPER_BOARD = "v1/cede-arena/tournaments#sniper",
  ARENA_TOURNAMENT_STREAK_BOARD = "v1/cede-arena/tournaments#streak",
  ARENA_TOURNAMENT_ME = "v1/cede-arena/tournaments#me",
  ARENA_TOURNAMENT_JOIN = "v1/cede-arena/tournaments#join",
  CMS_ARENA_TOURNAMENTS = "v1/cms/arena-tournaments",
  CMS_ARENA_TOURNAMENT_CREATE = "v1/cms/arena-tournaments#create",
  CMS_ARENA_TOURNAMENT_BY_ID = "v1/cms/arena-tournaments#by-id",
  CMS_ARENA_TOURNAMENT_UPDATE = "v1/cms/arena-tournaments#update",
  CMS_ARENA_TOURNAMENT_DELETE = "v1/cms/arena-tournaments#delete",
  CMS_ARENA_TOURNAMENT_ROUND_LEADERBOARD = "v1/cms/arena-tournaments#round-leaderboard",
  CMS_ARENA_TOURNAMENT_SNIPER_BOARD = "v1/cms/arena-tournaments#sniper",
  CMS_ARENA_TOURNAMENT_STREAK_BOARD = "v1/cms/arena-tournaments#streak",
  CMS_ARENA_TOURNAMENT_PARTICIPANTS = "v1/cms/arena-tournaments#participants",
  CMS_ARENA_TOURNAMENT_PARTICIPANT_DUELS = "v1/cms/arena-tournaments#participant-duels",
  CMS_ARENA_TOURNAMENT_VOIDED_DUELS = "v1/cms/arena-tournaments#voided-duels",
  // 204 POSTs under /:id (disqualify, reinstate, voided-duels, voided-duels/restore, distribute-now).
  CMS_ARENA_TOURNAMENT_ACTION = "v1/cms/arena-tournaments#action",
  CMS_ARENA_TICKET_LINK_GET_BY_PAGE = "v1/cms/arena-ticket-links/get-by-page",
  CMS_ARENA_TICKET_LINK_GET_BY_ID = "v1/cms/arena-ticket-links/get-by-id",
  CMS_ARENA_TICKET_LINK_SAVE = "v1/cms/arena-ticket-links/save",
  CMS_ARENA_TICKET_LINK_DELETE = "v1/cms/arena-ticket-links/delete",
  CMS_ARENA_BOT_SCHEDULER_CONFIG = "v1/cms/arena-bot-scheduler-config",
  CMS_ARENA_TRAFFIC_CURVE_CONFIG = "v1/cms/arena-traffic-curve-config",

  // Cede Arena virtual balance endpoints
  CEDE_ARENA_BALANCE = "v1/cede-arena-virtual-balance/balance",
  CEDE_ARENA_DEPOSIT_ADDRESS = "v1/cede-arena-virtual-balance/deposit-address",
  CEDE_ARENA_DEPOSIT = "v1/cede-arena-virtual-balance/deposit",
  CEDE_ARENA_DEPOSIT_METHOD = "v1/cede-arena-virtual-balance/deposit-method",
  CEDE_ARENA_DEPOSIT_METHODS = "v1/cede-arena-virtual-balance/deposit-methods",
  CEDE_ARENA_CROSSMINT_SETUP = "v1/cede-arena-virtual-balance/crossmint/setup",
  CEDE_ARENA_CROSSMINT_WALLET = "v1/cede-arena-virtual-balance/crossmint/wallet",
  CEDE_ARENA_CROSSMINT_WALLET_VERIFY = "v1/cede-arena-virtual-balance/crossmint/wallet/verify",
  CEDE_ARENA_CROSSMINT_ORDERS = "v1/cede-arena-virtual-balance/crossmint/orders",
  /** Takes the order id as a path param. Singular, so its GET response keeps its own schema. */
  CEDE_ARENA_CROSSMINT_ORDER = "v1/cede-arena-virtual-balance/crossmint/order",
  CEDE_ARENA_CROSSMINT_OPEN_ORDERS = "v1/cede-arena-virtual-balance/crossmint/order/open",
  CEDE_ARENA_WITHDRAW = "v1/cede-arena-virtual-balance/withdraw",
  CEDE_ARENA_WITHDRAWS = "v1/cede-arena-virtual-balance/withdraws",
  CEDE_ARENA_WITHDRAW_CLAIM = "v1/cede-arena-virtual-balance/withdraw/claim",
  CEDE_ARENA_MOVEMENTS = "v1/cede-arena-virtual-balance/movements",
  // Public proof-of-payout feed. Lives under the `cede-arena` prefix (with the
  // leaderboards) rather than `cede-arena-virtual-balance`, whose every other
  // route is auth-scoped to the caller.
  CEDE_ARENA_RECENT_PAYOUTS = "v1/cede-arena/payouts/recent",
  CEDE_ARENA_FEED = "v1/cede-arena/feed",

  // Cede Arena chat
  CEDE_ARENA_CHAT_MESSAGES = "v1/cede-arena-chat/messages",
  CEDE_ARENA_CHAT_SEND = "v1/cede-arena-chat/send",

  // Cede Arena support chat
  ARENA_SUPPORT_CONVERSATION = "v1/arena-support-chat/conversation",
  ARENA_SUPPORT_MESSAGES = "v1/arena-support-chat/messages",
  // Sends get their own path so one API_ENDPOINT key never has to carry two
  // response shapes. Mirrors the CEDE_ARENA_CHAT_SEND precedent.
  ARENA_SUPPORT_SEND = "v1/arena-support-chat/send",
  ARENA_SUPPORT_SEEN = "v1/arena-support-chat/seen",
  ARENA_SUPPORT_RESOLVE = "v1/arena-support-chat/resolve",
  ARENA_SUPPORT_RATE = "v1/arena-support-chat/rate",
  CMS_ARENA_SUPPORT_CONVERSATIONS = "v1/cms/arena-support-chat/conversations",
  // Private-key-guarded read of a conversation's message history — no ownership check (see ARENA_SUPPORT_MESSAGES).
  CMS_ARENA_SUPPORT_MESSAGES = "v1/cms/arena-support-chat/messages",
  CMS_ARENA_SUPPORT_REPLY = "v1/cms/arena-support-chat/reply",
  CMS_ARENA_SUPPORT_RESOLVE = "v1/cms/arena-support-chat/resolve",
  CMS_ARENA_SUPPORT_TAKE_OVER = "v1/cms/arena-support-chat/take-over",
  CMS_ARENA_SUPPORT_HAND_BACK = "v1/cms/arena-support-chat/hand-back",
  CMS_ARENA_SUPPORT_REVIEW_MESSAGE = "v1/cms/arena-support-chat/review-message",
  CMS_ARENA_SUPPORT_AGENT_KB = "v1/cms/arena-support-agent/kb",
  CMS_ARENA_SUPPORT_AGENT_KB_RETRY_EMBEDDING = "v1/cms/arena-support-agent/kb/retry-embedding",
  CMS_ARENA_SUPPORT_AGENT_PLAYGROUND = "v1/cms/arena-support-agent/playground",
  CMS_ARENA_SUPPORT_AGENT_RUNS = "v1/cms/arena-support-agent/runs",
  CMS_ARENA_SUPPORT_AGENT_GOLDEN = "v1/cms/arena-support-agent/golden",
  CMS_ARENA_SUPPORT_AGENT_GOLDEN_CREATE = "v1/cms/arena-support-agent/golden/create",
  CMS_ARENA_SUPPORT_AGENT_GOLDEN_DELETE = "v1/cms/arena-support-agent/golden/delete",
  CMS_ARENA_SUPPORT_AGENT_EVAL_RUN = "v1/cms/arena-support-agent/eval/run",
  CMS_ARENA_SUPPORT_AGENT_EVAL_RUNS = "v1/cms/arena-support-agent/eval/runs",
  // Call site passes `pathParams: [evalRunId]`.
  CMS_ARENA_SUPPORT_AGENT_EVAL_RUN_BY_ID = "v1/cms/arena-support-agent/eval/runs#by-id",
  CMS_ARENA_SUPPORT_AGENT_DASHBOARD = "v1/cms/arena-support-agent/dashboard",

  // Feature Flags CMS endpoints
  FEATURE_FLAGS_CMS_CREATE = "v1/cms/feature-flags/create",
  FEATURE_FLAGS_CMS_UPDATE = "v1/cms/feature-flags/update",
  FEATURE_FLAGS_CMS_DELETE = "v1/cms/feature-flags/delete",
  FEATURE_FLAGS_CMS_BULK_CREATE = "v1/cms/feature-flags/bulk-create",
  FEATURE_FLAGS_CMS_BULK_DELETE = "v1/cms/feature-flags/bulk-delete",
  FEATURE_FLAGS_CMS_GET_BY_PAGE = "v1/cms/feature-flags/get-by-page",

  // Hub Banner CMS endpoints
  HUB_BANNER_CMS_SAVE = "v1/cms/hub-banners/save",
  HUB_BANNER_CMS_DELETE = "v1/cms/hub-banners/delete",
  HUB_BANNER_CMS_GET_BY_ID = "v1/cms/hub-banners/get-by-id",
  HUB_BANNER_CMS_GET_BY_PAGE = "v1/cms/hub-banners/get-by-page",
  HUB_BANNERS_ACTIVE = "v1/hub-banners",

  // Trade and Earn Competition CMS endpoints
  CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_SAVE = "v1/cms/cede-flow-trade-and-earn-competitions/save",
  CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_DELETE = "v1/cms/cede-flow-trade-and-earn-competitions/delete",
  CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_GET_BY_ID = "v1/cms/cede-flow-trade-and-earn-competitions/get-by-id",
  CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_GET_BY_PAGE = "v1/cms/cede-flow-trade-and-earn-competitions/get-by-page",
  CEDE_FLOW_TRADE_AND_EARN_COMPETITIONS_ACTIVE = "v1/cede-flow-trade-and-earn-competitions",
  CEDE_FLOW_TRADE_AND_EARN_COMPETITION_BY_SLUG = "v1/cede-flow-trade-and-earn-competitions/by-slug",
  CEDE_FLOW_TRADE_AND_EARN_COMPETITION_BY_SLUG_INTERNAL = "v1/cede-flow-trade-and-earn-competitions/by-slug/internal",

  // Hub Update CMS endpoints
  HUB_UPDATE_CMS_SAVE = "v1/cms/hub-updates/save",
  HUB_UPDATE_CMS_DELETE = "v1/cms/hub-updates/delete",
  HUB_UPDATE_CMS_GET_BY_ID = "v1/cms/hub-updates/get-by-id",
  HUB_UPDATE_CMS_GET_BY_PAGE = "v1/cms/hub-updates/get-by-page",
  HUB_UPDATES_PUBLISHED = "v1/hub-updates",

  // Arena Announcement CMS endpoints
  ARENA_ANNOUNCEMENT_CMS_SAVE = "v1/cms/arena-announcements/save",
  ARENA_ANNOUNCEMENT_CMS_DELETE = "v1/cms/arena-announcements/delete",
  ARENA_ANNOUNCEMENT_CMS_GET_BY_ID = "v1/cms/arena-announcements/get-by-id",
  ARENA_ANNOUNCEMENT_CMS_GET_BY_PAGE = "v1/cms/arena-announcements/get-by-page",
  GEO_RESTRICTION_CMS_SAVE = "v1/cms/geo-restrictions/save",
  GEO_RESTRICTION_CMS_DELETE = "v1/cms/geo-restrictions/delete",
  GEO_RESTRICTION_CMS_GET_BY_PAGE = "v1/cms/geo-restrictions/get-by-page",
  ARENA_ANNOUNCEMENTS_PUBLISHED = "v1/arena-announcements",

  // Onboarding Step CMS endpoints
  ONBOARDING_STEP_CMS_SAVE = "v1/cms/onboarding-steps/save",
  ONBOARDING_STEP_CMS_DELETE = "v1/cms/onboarding-steps/delete",
  ONBOARDING_STEP_CMS_GET_BY_ID = "v1/cms/onboarding-steps/get-by-id",
  ONBOARDING_STEP_CMS_GET_BY_PAGE = "v1/cms/onboarding-steps/get-by-page",
  ONBOARDING_STEPS_PUBLISHED = "v1/onboarding-steps",

  // Trade Tutorial Step CMS endpoints
  TRADE_TUTORIAL_STEP_CMS_SAVE = "v1/cms/trade-tutorial-steps/save",
  TRADE_TUTORIAL_STEP_CMS_DELETE = "v1/cms/trade-tutorial-steps/delete",
  TRADE_TUTORIAL_STEP_CMS_GET_BY_ID = "v1/cms/trade-tutorial-steps/get-by-id",
  TRADE_TUTORIAL_STEP_CMS_GET_BY_PAGE = "v1/cms/trade-tutorial-steps/get-by-page",
  TRADE_TUTORIAL_STEPS_PUBLISHED = "v1/trade-tutorial-steps",

  // Spinning Wheel Config CMS endpoints
  SPINNING_WHEEL_CONFIG_CMS_SAVE = "v1/cms/spinning-wheel-configs/save",
  SPINNING_WHEEL_CONFIG_CMS_DELETE = "v1/cms/spinning-wheel-configs/delete",
  SPINNING_WHEEL_CONFIG_CMS_GET_BY_ID = "v1/cms/spinning-wheel-configs/get-by-id",
  SPINNING_WHEEL_CONFIG_CMS_GET_BY_PAGE = "v1/cms/spinning-wheel-configs/get-by-page",

  // CMS Permissions endpoints
  CMS_PERMISSIONS_ME = "v1/cms/permissions/me",
  CMS_PERMISSIONS_USERS = "v1/cms/permissions/users",

  // Campaign Profitability Checker endpoints
  ADMIN_CPC_CREATE_SIMULATION = "v1/admin/campaign-profitability-checker/simulations",
  ADMIN_CPC_LIST_SIMULATIONS = "v1/admin/campaign-profitability-checker/simulations/list",
  ADMIN_CPC_CANCEL_SIMULATION = "v1/admin/campaign-profitability-checker/simulations/cancel",
  ADMIN_CPC_VERIFY_WALLET = "v1/admin/campaign-profitability-checker/verify-wallet",
  ADMIN_CPC_WALLET_PORTFOLIO = "v1/admin/campaign-profitability-checker/wallet-portfolio",

  // Self-Register Campaigns endpoints
  HUB_SELF_REGISTER_CAMPAIGNS_SUBMIT = "v1/self-register-campaigns/submit",

  // Crypto.com Referee endpoints
  HUB_CRYPTOCOM_REFEREE_SUBMIT = "v1/hub/cryptocom-referee/submit",

  // Airdrop Farming endpoints
  HUB_AIRDROP_FARMING_BOTS = "v1/hub/airdrop-farming/bots",
  /**
   * v2 of the bot list endpoint. Returns the paginated envelope
   * `{ bots, hasMore, nextOffset }`. v1 stays on the legacy bare-array
   * shape for older Flow clients that don't ship the new schema.
   */
  HUB_AIRDROP_FARMING_BOTS_V2 = "v2/hub/airdrop-farming/bots",
  HUB_AIRDROP_FARMING_BOT_AGGREGATES = "v1/hub/airdrop-farming/bots/aggregates",
  HUB_AIRDROP_FARMING_BOT_PERFORMANCE = "v1/hub/airdrop-farming/bots/performance",
  HUB_AIRDROP_FARMING_MIN_CAPITAL = "v1/hub/airdrop-farming/bots/min-capital",
  HUB_AIRDROP_FARMING_AVAILABLE_CAPITAL = "v1/hub/airdrop-farming/bots/available-capital",
  HUB_AIRDROP_FARMING_SUGGEST_PAIRS = "v1/hub/airdrop-farming/bots/suggest-pairs",
  HUB_AIRDROP_FARMING_FEE_TIER = "v1/hub/airdrop-farming/bots/fee-tier",
  HUB_AIRDROP_FARMING_BUILDER_FEES = "v1/hub/airdrop-farming/bots/builder-fees",
  HUB_AIRDROP_FARMING_LEADERBOARD = "v1/hub/airdrop-farming/leaderboard",
  HUB_AIRDROP_FARMING_LEADERBOARD_STATS = "v1/hub/airdrop-farming/leaderboard/stats",
  HUB_AIRDROP_FARMING_LEADERBOARD_POSITION = "v1/hub/airdrop-farming/leaderboard/position",
  HUB_AIRDROP_FARMING_PENDING_RATING = "v1/hub/airdrop-farming/bots/pending-rating",
  HUB_AIRDROP_FARMING_BOT_RATING = "v1/hub/airdrop-farming/bots#rating",
  HUB_AIRDROP_FARMING_BOT_HIDE = "v1/hub/airdrop-farming/bots/hide",
  HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD = "v1/hub/airdrop-farming/order-fill-leaderboard",
  HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD_POSITION = "v1/hub/airdrop-farming/order-fill-leaderboard/position",
  HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD_STATS = "v1/hub/airdrop-farming/order-fill-leaderboard/stats",
  HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD_VOLUMES = "v1/hub/airdrop-farming/order-fill-leaderboard/volumes",
  HUB_AIRDROP_FARMING_MAINTENANCE_WINDOWS = "v1/hub/airdrop-farming/bots/maintenance-windows",

  // Delta-Neutral endpoints
  HUB_DELTA_NEUTRAL_BOTS = "v1/hub/airdrop-farming/delta-neutral/bots",
  HUB_DELTA_NEUTRAL_LEG_INFO = "v1/hub/airdrop-farming/delta-neutral/bots/leg-info",
  HUB_DELTA_NEUTRAL_BOT_HIDE = "v1/hub/airdrop-farming/delta-neutral/bots/hide",

  // Funding Rates
  FUNDING_RATE_SUMMARIES = "v1/funding-arbitrage/summaries",

  // Paper Trading endpoints
  HUB_PAPER_TRADING_PLACE_ORDER = "v1/hub/paper-trading/orders",
  // DELETE /orders/:externalOrderId — disambiguated from the place-order POST
  // via a hash suffix that `ApiHelper.makeRequest` strips before constructing
  // the URL. `pathParams: [externalOrderId]` is passed at the call site.
  HUB_PAPER_TRADING_CANCEL_ORDER = "v1/hub/paper-trading/orders#cancel",
  // DELETE /orders/:orderId/triggers — keyed off the internal paper-order
  // UUID (distinct from the cancel-order route which keys off externalOrderId).
  // Call site passes `pathParams: [orderId, "triggers"]`.
  HUB_PAPER_TRADING_CANCEL_ATTACHED_TRIGGERS = "v1/hub/paper-trading/orders#cancel-triggers",
  HUB_PAPER_TRADING_PREVIEW_ORDER = "v1/hub/paper-trading/orders/preview",
  HUB_PAPER_TRADING_ACCOUNT = "v1/hub/paper-trading/account",
  HUB_PAPER_TRADING_POSITIONS = "v1/hub/paper-trading/positions",
  // POST — declarative SET / EDIT of a position's TP/SL. campaign + pairSymbol
  // travel in the body (pairSymbol carries slashes + a colon, not URL-safe).
  HUB_PAPER_TRADING_SET_POSITION_TRIGGERS = "v1/hub/paper-trading/positions/triggers",
  // DELETE — clear a position's resting TP/SL (all kinds, or one). Hash suffix
  // disambiguates it from the POST set route; `makeRequest` strips it.
  HUB_PAPER_TRADING_CLEAR_POSITION_TRIGGERS = "v1/hub/paper-trading/positions/triggers#clear",
  HUB_PAPER_TRADING_OPEN_ORDERS = "v1/hub/paper-trading/orders/open",
  HUB_PAPER_TRADING_FILLS = "v1/hub/paper-trading/fills",
  HUB_PAPER_TRADING_OHLCV = "v1/hub/paper-trading/ohlcv",
  HUB_PAPER_TRADING_RWA_VENUES = "v1/hub/paper-trading/rwa-venues",
  HUB_PAPER_TRADING_PAIR_METAS = "v1/hub/paper-trading/pair-metas",
  HUB_PAPER_TRADING_PAIR_VOLATILITY = "v1/hub/paper-trading/pair-volatility",
}

export enum PUBLIC_API_ENDPOINT {
  ORDERBOOK_SUBSCRIBE = "order-book-subscription/subscribe",
}
